import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Calendar } from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

const BID_ACTION_TYPES = [
  { value: 'renegotiate_price', label: 'Renegotiate Price' },
  { value: 'request_requote', label: 'Request Requote' },
  { value: 'source_alternate_supplier', label: 'Source Alternate Supplier' },
  { value: 'consolidate_volume', label: 'Consolidate Volume' },
  { value: 'approve_award', label: 'Approve & Award' },
];

const QuickAssignModal = ({ open, onClose, context = {} }) => {
  const [users, setUsers] = useState([]);
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('medium');
  const [actionType, setActionType] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/actions/users').then(res => setUsers(res.data.users || [])).catch(() => {});
      const d = new Date();
      d.setDate(d.getDate() + 3);
      setDueDate(d.toISOString().split('T')[0]);
      setNote(context.recommendation || '');
      setActionType(context.actionType || '');
    }
  }, [open, context.recommendation, context.actionType]);

  const handleSubmit = async () => {
    if (!assignee) { toast.error('Please select an assignee'); return; }
    const selectedUser = users.find(u => u.email === assignee);
    setSubmitting(true);
    try {
      await api.post('/actions/assign', {
        assignToName: selectedUser?.name || assignee,
        assignToEmail: assignee,
        department: selectedUser?.department || '',
        priority,
        dueDate: new Date(dueDate).toISOString(),
        details: note || context.recommendation || `Action from ${context.sourceModule || 'platform'}`,
        sourceModule: context.sourceModule || '',
        actionType: actionType || 'internal',
        context: {
          mpn: context.mpn || '',
          bomName: context.bomName || '',
          bomId: context.bomId || '',
          componentId: context.componentId || '',
          recommendation: context.recommendation || '',
          sourceModule: context.sourceModule || '',
          savings: context.savings || '',
        },
      });
      toast.success(`Action assigned to ${selectedUser?.name || assignee}`);
      setAssignee('');
      setPriority('medium');
      setActionType('');
      setNote('');
      onClose();
    } catch {
      toast.error('Failed to assign action');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="quick-assign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-green-600" />
            Assign Action
          </DialogTitle>
        </DialogHeader>

        {/* Context Badge */}
        {(context.mpn || context.bomName || context.sourceModule) && (
          <div className="flex flex-wrap gap-1.5 -mt-2">
            {context.sourceModule && <Badge variant="outline" className="text-xs">{context.sourceModule}</Badge>}
            {context.mpn && <Badge className="bg-blue-100 text-blue-800 text-xs font-mono">{context.mpn}</Badge>}
            {context.bomName && <Badge className="bg-green-100 text-green-800 text-xs">{context.bomName}</Badge>}
          </div>
        )}

        <div className="space-y-4 py-2">
          <div>
            <Label>Assignee <span className="text-red-500">*</span></Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger className="mt-1" data-testid="assign-user-select">
                <SelectValue placeholder="Select team member..." />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.email} value={u.email}>
                    <span className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">
                        {u.name?.split(' ').map(n => n[0]).join('') || '?'}
                      </span>
                      {u.name} <span className="text-gray-400 text-xs">({u.department})</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="assign-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Due Date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1" data-testid="assign-due-date" />
            </div>
          </div>

          {BID_ACTION_TYPES.some(t => t.value === actionType) && (
            <div>
              <Label>Action Type</Label>
              <Select value={actionType} onValueChange={setActionType}>
                <SelectTrigger className="mt-1" data-testid="assign-action-type-select">
                  <SelectValue placeholder="Select action type..." />
                </SelectTrigger>
                <SelectContent>
                  {BID_ACTION_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Note / Instructions</Label>
            <Textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Add context or instructions for the assignee..."
              rows={3}
              className="mt-1"
              data-testid="assign-note-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !assignee}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="assign-submit-btn"
          >
            {submitting ? 'Assigning...' : 'Assign Action'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAssignModal;
